/**
 * sky_sampler — the Hillaire sky read on the CPU (2026-09-25, realism §8
 * R1/R2/R5/R6a): the SAME maths the engine bakes its sky-view LUT from
 * (`evaluateSkyViewRadiance` over a transmittance + multi-scatter LUT pair),
 * evaluated at a handful of directions so the key light, the hemisphere, the
 * fog, the clouds' ambient and the exposure curve all take their numbers from
 * the sky that is drawn — never from a band table.
 *
 * Every value is PER UNIT E0 (the sky's `sunIlluminance` = 1): multiply by
 * E0_LUX for lux, then by `luxToScene` for scene units. The sky is symmetric
 * about the sun's vertical plane, so a result depends on the sun's ELEVATION
 * only — cached on a 0.05° bucket (a 2 h day moves it that far in ~1.7 s).
 *
 * Small private LUTs (64×16 transmittance, 32² multi-scatter: ~30 ms once)
 * keep it backend-independent — WebGL2's inline sky has no LUT set to borrow.
 */
import { EARTH_ATMOSPHERE, type AtmosphereConfig } from 'enginex/render/sky/AtmosphereConfig.js';
import { ATLAS_ATMOSPHERE } from './atmosphere.js';
import { bakeTransmittanceLut, transmittanceToTopOfAtmosphere } from 'enginex/render/sky/lut/TransmittanceLut.js';
import { bakeMultiScatterLut } from 'enginex/render/sky/lut/MultiScatterLut.js';
import { evaluateSkyViewRadiance, type SkyViewContext } from 'enginex/render/sky/lut/SkyViewLut.js';
import { NATURAL_SQM, artificialSkyRatio, domeRadiance, type LightDome, type Skyglow } from '../light_pollution.js';
import { skyglowLive } from '../light_pollution_live.js';
import { lightFrame } from '../photometry.js';
import { UNIT_NITS, naturalShape, naturalTint, naturalSkyLift, glowShape, nightSkyUnits } from './night_sky_model.js';

export interface V3 { x: number; y: number; z: number }
export interface SkySample {
  sinEl: number;
  /** Horizontal irradiance from the whole upper sky (per unit E0). */
  irradiance: V3;
  /** Mean radiance of the upper hemisphere (per unit E0) — an isotropic medium's sky in-scatter. */
  meanRadiance: V3;
  zenith: V3;
  /** Horizon ring (1° up): the azimuthal MEDIAN (the aureole excluded), and toward / away from the sun. */
  horizon: V3; horizonSun: V3; horizonAnti: V3;
}

const RINGS = 7, AZ = 12;

export class SkySampler {
  private ctx: SkyViewContext | null = null;
  private readonly cache = new Map<number, SkySample>();
  // ATLAS_ATMOSPHERE (2026-09-27): the aerosol-carrying config the sky is DRAWN with — the sampler must read the same air.
  constructor(private readonly cfg: AtmosphereConfig = ATLAS_ATMOSPHERE, private readonly viewHeightKm = EARTH_ATMOSPHERE.bottomRadius + 0.001) {}

  private context(): SkyViewContext {
    if (this.ctx) return this.ctx;
    const tt = bakeTransmittanceLut(this.cfg, 64, 16, 24);
    // 32² (lighting, 2026-09-26): a 16-row Ψ_ms (3.75 km altitude steps) read the low air's multiple scattering 16 %
    // bright on the 1° ring at sunset and 33 % at −3° against the dome's canonical 32² — the fog (this ring) glowed over
    // the twilight horizon. The dome's own directions and steps (2026-10-06, THE LOCAL SUN): once every sample reads its
    // own sun angle, a twilight Ψ_ms comes from a narrow cone of lit air, and 12 directions put the fog's ring 34 % over
    // the dome at −3° and 2.8× at −6° (lighting-physical's silhouette band); the canonical bake is within 1.4 %.
    const ms = bakeMultiScatterLut({ cfg: this.cfg, transmittanceData: tt.data, transmittanceWidth: tt.width, transmittanceHeight: tt.height }, 32);
    tt.texture.dispose(); ms.texture.dispose();
    this.ctx = {
      cfg: this.cfg, viewHeight: this.viewHeightKm, cosSunZenith: 1,
      transmittanceData: tt.data, transmittanceWidth: tt.width, transmittanceHeight: tt.height,
      multiScatterData: ms.data, multiScatterSize: ms.width, sunIlluminance: 1,
    };
    return this.ctx;
  }

  /** Atmospheric transmittance toward the sun from `altKm` above the ground (the key light's colour). */
  transmittance(sinEl: number, altKm = 0.001): V3 {
    return transmittanceToTopOfAtmosphere(this.cfg, this.cfg.bottomRadius + Math.max(0.001, altKm), Math.max(-1, Math.min(1, sinEl)));
  }

  /** The sky at a sun elevation (cached per 0.05°). */
  sample(sinEl: number): SkySample {
    const s = Math.max(-1, Math.min(1, sinEl));
    const key = Math.round((Math.asin(s) * 180) / Math.PI / 0.05);
    const hit = this.cache.get(key);
    if (hit) return hit;
    const out = this.evaluate(Math.sin((key * 0.05 * Math.PI) / 180));
    if (this.cache.size > 4096) this.cache.clear();
    this.cache.set(key, out);
    return out;
  }

  private evaluate(sinEl: number): SkySample {
    const ctx = this.context();
    ctx.cosSunZenith = sinEl;
    const cS = Math.sqrt(Math.max(0, 1 - sinEl * sinEl)); // the sun's horizontal component (sun at azimuth 0)
    const L = (mu: number, dx: number): V3 => evaluateSkyViewRadiance(ctx, mu, dx * cS * Math.sqrt(Math.max(0, 1 - mu * mu)) + mu * sinEl);
    const irr = { x: 0, y: 0, z: 0 }, mean = { x: 0, y: 0, z: 0 };
    let wMean = 0;
    for (let i = 0; i < RINGS; i++) {
      const mu = (i + 0.5) / RINGS;           // uniform in µ = equal solid angle bands
      for (let j = 0; j < AZ; j++) {
        const az = ((j + 0.5) / AZ) * Math.PI * 2;
        const r = L(mu, Math.cos(az));
        const w = (1 / RINGS) * ((Math.PI * 2) / AZ); // dΩ per sample (dµ·dφ)
        irr.x += r.x * mu * w; irr.y += r.y * mu * w; irr.z += r.z * mu * w;
        mean.x += r.x * w; mean.y += r.y * w; mean.z += r.z * w; wMean += w;
      }
    }
    mean.x /= wMean; mean.y /= wMean; mean.z /= wMean;
    const muH = Math.sin((1 * Math.PI) / 180); // where distant terrain meets the sky
    // The ring's azimuthal MEDIAN, not its mean (2026-09-27, the coordinator's review): with a real aerosol column
    // the aureole beside a low sun is 10–15× the side sky on this ring, and a mean of twelve azimuths is a third
    // aureole — the fog cut from it sat 2.7× over the dome beside and behind the sun (the harness's silhouette
    // band). The median is the sky the fog meets at every azimuth but the sun's; the height fog's own HG lobe
    // paints the glow toward the sun.
    const ring: V3[] = [];
    for (let j = 0; j < AZ; j++) ring.push(L(muH, Math.cos(((j + 0.5) / AZ) * Math.PI * 2)));
    const med = (k: 'x' | 'y' | 'z'): number => {
      const v = ring.map((r) => r[k]).sort((a, b) => a - b);
      return (v[AZ / 2 - 1] + v[AZ / 2]) * 0.5;
    };
    const horizon = { x: med('x'), y: med('y'), z: med('z') };
    return { sinEl, irradiance: irr, meanRadiance: mean, zenith: L(1, 0), horizon, horizonSun: L(muH, 1), horizonAnti: L(muH, -1) };
  }
}

/** One shared sampler (ATLAS_ATMOSPHERE — the air Atlas's sky is created with). */
let shared: SkySampler | null = null;
export function skySampler(): SkySampler { return (shared ??= new SkySampler()); }

// ── THE NIGHT SKY ON THE CPU (the night-sky realism wave, 2026-10-06 — agent A) ──────────────────────────────────────
// The Hillaire sample above is the SUN's sky, per unit E0: at night it is black. What lights a night's AIR is the night
// sky the sky shader draws (lib/sky/night_sky.ts): the NATURAL sky (airglow, starlight, zodiacal light — never black)
// at its display LIFT, and the ARTIFICIAL sky the settlements put up (lib/light_pollution.ts — the contract every system
// reads). This is their CPU mirror for the fog's colour, the clouds and the night ambient, in luminance units (cd/m²)
// — multiply by `luxToScene` (or pass through `photometry.emissiveNits`) for scene units.
// THE DRAWN SKY (2026-10-06, the coordinator after agent S's hand-back): a COLOUR is read in the units the sky shader
// DRAWS — night_sky_model.nightSkyUnits: the natural sky × the lift × the dark-adapted gain, the artificial sky ADAPTED
// (`artificialDisplayUnits`, 2·ln(1 + R/2)) × the lift — so the fog in front of the sky is the sky's own colour (the
// first cut used the contract's R unlifted: the haze over a town was 2.4–20× dimmer than the sky drawn behind it). An
// ILLUMINANCE (the town's glow on the ground) stays PHYSICAL (`phys`, `artLux`): the ground keeps its own floor lift.
// The natural and artificial terms are night_sky_model's (the shader's own CPU mirror, never re-derived here); what this
// adds is the DECK: a cloud deck over a lit town reflects its upward light back down (OVERCAST_AMPLIFICATION — in the
// physical R, adapted after) and reddens it, and hides most of the natural sky.

/** The natural night sky's zenith luminance (cd/m²): the contract's unit — `sqmToNits(22.0)` ≈ 1.71·10⁻⁴. */
export const NIGHT_SKY_NITS = UNIT_NITS;
/**
 * The A/B switches of the night air (probes: `__nightAir(false)` draws the BEFORE in the same frame conditions):
 * `fog` — the night sky's light in the fog (AtmosphereModule); `glow` — the town's glow on the ground (LightingModule).
 * The clouds' switches are cloud_light's (`cloudUplight.enabled`, `cloudNight.physical`); the lever flips all four.
 */
export const nightAir = { fog: true, glow: true };
/**
 * THE OVERCAST AMPLIFICATION: a cloud deck over a lit town reflects the town's upward light back down — the overcast
 * ARTIFICIAL zenith is this × the clear one (Kyba et al. 2011: ×10.1 inside Berlin, ×2.8 at 32 km; Jechow et al. 2017:
 * ×3–6 under a 6 km base; Jechow & Kyba 2022: ×25 at Berlin's centre). 8 fits Atlas's low (0.9–2 km) decks: a city's
 * overcast sky reads ~16 mag/arcsec² (its clear 18.3). Toward the horizon the deck is farther and dimmer (`deckShape`).
 */
export const OVERCAST_AMPLIFICATION = 8;
/** Of the natural sky, what a full deck leaves (the airglow is ABOVE the clouds — a dark site's overcast is darker than its clear sky; Kyba 2011). */
export const OVERCAST_NATURAL_LEFT = 0.25;
/** Overcast skyglow is REDDER (Kyba et al. 2012, "Red is the new black": in Berlin's overcast the blue rose ×7, the red ×18) — a hue shift on the glow tint. */
export const OVERCAST_TINT_SHIFT: readonly [number, number, number] = [1.22, 1.0, 0.7];

const lumV = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;
/** The glow tint under a deck (luminance kept). */
export function overcastTint(t: readonly number[]): [number, number, number] {
  const r = t[0] * OVERCAST_TINT_SHIFT[0], g = t[1] * OVERCAST_TINT_SHIFT[1], b = t[2] * OVERCAST_TINT_SHIFT[2];
  const l = Math.max(1e-6, lumV(r, g, b)), l0 = Math.max(1e-6, lumV(t[0], t[1], t[2]));
  return [(r / l) * l0, (g / l) * l0, (b / l) * l0];
}
/** A deck's luminance by elevation relative to its zenith (the far deck is dimmer — Jechow & Kyba 2022: ×25 at the zenith, ×11 in illuminance). */
export function deckShape(sinEl: number): number { const s = Math.max(0, Math.min(1, sinEl)); return 0.55 + 0.45 * s; }

type Dome = Pick<LightDome, 'dirX' | 'dirZ' | 'peak' | 'halfWidth' | 'height' | 'tint'>;
/** No artificial sky (the drawn natural sky alone through S's mirror). */
const NATURAL_ONLY: Pick<Skyglow, 'ratio' | 'tint'> = { ratio: 0, tint: [1, 1, 1] };

/**
 * The night sky along a unit direction AS DRAWN (cd/m²-equivalent, RGB into `out`) — at `cloudiness` 0 exactly
 * night_sky_model.nightSkyUnits(glow, domes, lift, v).rgb × UNIT_NITS (`verify:night-air` pins it: S's mirror is CALLED,
 * never re-derived, so a change to the display model reaches the fog the same day). `art` (optional) receives the drawn
 * artificial part alone; `phys` (optional) the PHYSICAL artificial luminance (the contract's R × the deck, unlifted,
 * unadapted — for an illuminance). Under a deck (`cloudiness` 0..1) the physical artificial sky is amplified
 * (OVERCAST_AMPLIFICATION × deckShape) and reddened BEFORE S's display mapping adapts it, and the natural sky is mostly
 * hidden (OVERCAST_NATURAL_LEFT).
 */
export function nightSkyRadiance(glow: Skyglow, domes: ReadonlyArray<Dome>, vx: number, vy: number, vz: number, cloudiness: number, lift: number, out: V3, art?: V3, phys?: V3): V3 {
  const c = Math.max(0, Math.min(1, Number.isFinite(cloudiness) ? cloudiness : 0));
  const s = Math.max(0, vy);
  const L = Number.isFinite(lift) && lift > 0 ? lift : 1;
  const nd = nightSkyUnits(NATURAL_ONLY, [], L, vx, vy, vz).rgb; // the drawn natural sky (S's mirror)
  const left = (1 - (1 - OVERCAST_NATURAL_LEFT) * c) * NIGHT_SKY_NITS;
  const R = artificialSkyRatio(glow, domes as ReadonlyArray<LightDome>, vx, vy, vz);
  let ar = 0, ag = 0, ab = 0, pr = 0, pg = 0, pb = 0;
  if (R > 0) {
    const u = glow.ratio * glowShape(s);
    let w = u, tr = u * glow.tint[0], tg = u * glow.tint[1], tb = u * glow.tint[2];
    for (const d of domes) { const dr = domeRadiance(d, vx, vy, vz); if (dr > 0) { w += dr; tr += dr * d.tint[0]; tg += dr * d.tint[1]; tb += dr * d.tint[2]; } }
    const tint = w > 0 ? [tr / w, tg / w, tb / w] : [glow.tint[0], glow.tint[1], glow.tint[2]];
    const ot = overcastTint(tint);
    const amp = 1 + (OVERCAST_AMPLIFICATION * deckShape(vy) - 1) * c;
    const tc0 = tint[0] + (ot[0] - tint[0]) * c, tc1 = tint[1] + (ot[1] - tint[1]) * c, tc2 = tint[2] + (ot[2] - tint[2]) * c;
    const pl = R * amp * NIGHT_SKY_NITS; // physical
    pr = pl * tc0; pg = pl * tc1; pb = pl * tc2;
    // drawn: S's own mapping of the (deck-amplified) artificial sky along this ray — one source standing for the glow and
    // the domes (its ratio is R × amp ÷ the glow shape, so the mirror's `g` is R × amp; its tint the ratio-weighted one).
    const t3: [number, number, number] = [tc0, tc1, tc2];
    const dr = nightSkyUnits({ ratio: (R * amp) / glowShape(s), tint: t3 }, [], L, vx, vy, vz).rgb;
    ar = Math.max(0, dr[0] - nd[0]) * NIGHT_SKY_NITS; ag = Math.max(0, dr[1] - nd[1]) * NIGHT_SKY_NITS; ab = Math.max(0, dr[2] - nd[2]) * NIGHT_SKY_NITS;
  }
  if (art) { art.x = ar; art.y = ag; art.z = ab; }
  if (phys) { phys.x = pr; phys.y = pg; phys.z = pb; }
  out.x = nd[0] * left + ar; out.y = nd[1] * left + ag; out.z = nd[2] * left + ab;
  return out;
}

export interface NightSkySample {
  /** The DRAWN sky (cd/m²-equivalent, RGB — the colour the fog takes): the zenith; the 1°-up ring's azimuthal median and mean; toward the strongest dome (1° up). */
  zenith: V3; horizonMedian: V3; horizonMean: V3; horizonToward: V3;
  /** The upper hemisphere's mean DRAWN luminance (an isotropic medium's in-scatter), and its natural and artificial parts. */
  mean: V3; meanNat: V3; meanArt: V3;
  /** Horizontal illuminance of the DRAWN sky (lux-equivalent, RGB — lum709 of it is the lux). */
  irradiance: V3;
  /** The ARTIFICIAL sky's share: its horizontal illuminance (lux — PHYSICAL, never lifted) and its colour (luminance 1). */
  artLux: number; artTint: [number, number, number];
  /** The natural sky's horizontal illuminance (lux — PHYSICAL: no lift, no display gain). */
  natLux: number;
  /** The strongest dome's horizontal direction (null without one), the deck's cloudiness and the lift sampled under. */
  domeDir: { x: number; z: number; halfWidth: number } | null;
  cloudiness: number; lift: number;
}

const NIGHT_RINGS = 8, NIGHT_AZ = 16;
/**
 * The night sky at the viewer: zenith, horizon ring (median / mean / toward the strongest dome), the hemisphere's mean
 * and the horizontal illuminance (µ-band quadrature, equal solid angle). Pure; ~300 evaluations.
 */
export function nightSkySample(glow: Skyglow, domes: ReadonlyArray<Dome>, cloudiness: number, lift = 1): NightSkySample {
  const o: V3 = { x: 0, y: 0, z: 0 }, a: V3 = { x: 0, y: 0, z: 0 }, p: V3 = { x: 0, y: 0, z: 0 };
  const irr = { x: 0, y: 0, z: 0 }, mean = { x: 0, y: 0, z: 0 }, meanArt = { x: 0, y: 0, z: 0 };
  let art = 0, nat = 0, atr = 0, atg = 0, atb = 0;
  const cl = Math.max(0, Math.min(1, Number.isFinite(cloudiness) ? cloudiness : 0));
  const natLeft = 1 - (1 - OVERCAST_NATURAL_LEFT) * cl;
  const dW = (1 / NIGHT_RINGS) * ((Math.PI * 2) / NIGHT_AZ);
  for (let i = 0; i < NIGHT_RINGS; i++) {
    const mu = (i + 0.5) / NIGHT_RINGS;
    const ct = Math.sqrt(1 - mu * mu);
    for (let j = 0; j < NIGHT_AZ; j++) {
      const az = ((j + 0.5) / NIGHT_AZ) * Math.PI * 2;
      nightSkyRadiance(glow, domes, ct * Math.sin(az), mu, ct * Math.cos(az), cloudiness, lift, o, a, p);
      irr.x += o.x * mu * dW; irr.y += o.y * mu * dW; irr.z += o.z * mu * dW;
      mean.x += o.x * dW; mean.y += o.y * dW; mean.z += o.z * dW;
      meanArt.x += a.x * dW; meanArt.y += a.y * dW; meanArt.z += a.z * dW;
      // THE ILLUMINANCE IS PHYSICAL: the artificial sky's lux from the contract's R (× the deck), the natural sky's from
      // its physical shape — never the drawn units (the ground keeps its own lift; LightingModule soft-caps the glow).
      const pl = lumV(p.x, p.y, p.z);
      art += pl * mu * dW; atr += p.x * mu * dW; atg += p.y * mu * dW; atb += p.z * mu * dW;
      const ntv = naturalTint(mu);
      nat += naturalShape(mu) * NIGHT_SKY_NITS * natLeft * lumV(ntv[0], ntv[1], ntv[2]) * mu * dW;
    }
  }
  const tw = 1 / (2 * Math.PI);
  mean.x *= tw; mean.y *= tw; mean.z *= tw; meanArt.x *= tw; meanArt.y *= tw; meanArt.z *= tw;
  const muH = Math.sin(Math.PI / 180), ctH = Math.cos(Math.PI / 180);
  const ring: V3[] = [];
  const hm = { x: 0, y: 0, z: 0 };
  for (let j = 0; j < 24; j++) {
    const az = ((j + 0.5) / 24) * Math.PI * 2;
    const r = nightSkyRadiance(glow, domes, ctH * Math.sin(az), muH, ctH * Math.cos(az), cloudiness, lift, { x: 0, y: 0, z: 0 });
    ring.push(r); hm.x += r.x / 24; hm.y += r.y / 24; hm.z += r.z / 24;
  }
  const med = (k: 'x' | 'y' | 'z'): number => { const v = ring.map((r) => r[k]).sort((p, q) => p - q); return (v[11] + v[12]) * 0.5; };
  const d0 = domes.find((d) => d.dirX !== 0 || d.dirZ !== 0) ?? null;
  const toward = d0
    ? nightSkyRadiance(glow, domes, ctH * d0.dirX, muH, ctH * d0.dirZ, cloudiness, lift, { x: 0, y: 0, z: 0 })
    : { x: med('x'), y: med('y'), z: med('z') };
  const al = Math.max(1e-12, lumV(atr, atg, atb));
  return {
    zenith: nightSkyRadiance(glow, domes, 0, 1, 0, cloudiness, lift, { x: 0, y: 0, z: 0 }),
    horizonMedian: { x: med('x'), y: med('y'), z: med('z') }, horizonMean: hm, horizonToward: toward,
    mean, meanArt, meanNat: { x: mean.x - meanArt.x, y: mean.y - meanArt.y, z: mean.z - meanArt.z }, irradiance: irr,
    artLux: art, artTint: art > 0 ? [atr / al, atg / al, atb / al] : [glow.tint[0], glow.tint[1], glow.tint[2]], natLux: nat,
    domeDir: d0 ? { x: d0.dirX, z: d0.dirZ, halfWidth: d0.halfWidth } : null,
    cloudiness: Math.max(0, Math.min(1, Number.isFinite(cloudiness) ? cloudiness : 0)), lift,
  };
}

const NO_GLOW: Skyglow = { ratio: 0, sqm: NATURAL_SQM, bortle: 1, nelm: 6.6, tint: [1.32, 0.94, 0.55] };
/** The live samples, keyed by the skyglow's update stamp, the lift and a 5 % cloudiness bucket (the writer is ~4 Hz; the readers — the fog at 1 Hz, the light per frame — ask for one or two buckets). */
const liveNight = { at: -1, active: false, lift: -1, byC: new Map<number, NightSkySample>() };
/**
 * The night sky over the camera now: `skyglowLive()` (the contract's store) at the shader's display lift for the moon
 * now (`night_sky_model.naturalSkyLift` — 25 under a new moon, 1 under a bright one).
 */
export function nightSkyLive(cloudiness: number): NightSkySample {
  const g = skyglowLive();
  const c = Math.round(Math.max(0, Math.min(1, Number.isFinite(cloudiness) ? cloudiness : 0)) * 20) / 20;
  const lift = Math.round(naturalSkyLift(lightFrame.moon?.model.globalLux ?? 0) * 100) / 100;
  if (liveNight.at !== g.at || liveNight.active !== g.active || liveNight.lift !== lift) { liveNight.at = g.at; liveNight.active = g.active; liveNight.lift = lift; liveNight.byC.clear(); }
  let s = liveNight.byC.get(c);
  if (!s) { s = nightSkySample(g.active ? g.glow : NO_GLOW, g.active ? g.domes : [], c, lift); liveNight.byC.set(c, s); }
  return s;
}
